import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../../lib/supabase/auth';
import { isSupabaseConfigured } from '../../lib/supabase/client';

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function checkAuth() {
      try {
        if (!isSupabaseConfigured) {
          setLoading(false);
          return;
        }

        const sess = await auth.getSession();
        if (mounted) {
          setSession(sess);
          if (sess?.user) {
            setUser(sess.user);
            const adminStatus = await auth.verifyAdmin(sess.user.id);
            setIsAdmin(adminStatus);
          } else {
            setUser(null);
            setIsAdmin(false);
          }
        }
      } catch (err) {
        console.error('Auth verification error:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    checkAuth();

    const { data: authListener } = auth.onAuthStateChange(async (event, newSession) => {
      if (!mounted) return;
      setSession(newSession);
      if (newSession?.user) {
        setUser(newSession.user);
        const adminStatus = await auth.verifyAdmin(newSession.user.id);
        setIsAdmin(adminStatus);
      } else {
        setUser(null);
        setIsAdmin(false);
      }
      setLoading(false);
    });

    return () => {
      mounted = false;
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  const login = async (email, password) => {
    const data = await auth.signIn(email, password);
    setUser(data.user);
    setSession(data.session);
    setIsAdmin(true);
    return data;
  };

  const logout = async () => {
    await auth.signOut();
    setUser(null);
    setSession(null);
    setIsAdmin(false);
  };

  const value = {
    user,
    session,
    loading,
    isAdmin,
    isConfigured: isSupabaseConfigured,
    login,
    logout,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
}

export default AdminAuthContext;
